import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { PaymentMethodsSection } from '../components/PaymentMethodsSection';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * Payment methods management (PD-14080) — the full list with add/remove/set
 * primary, moved off the account home (which now shows only the primary
 * method with a link here).
 *
 * The page owns the title row AND the Add button (rendered beside the
 * title on both breakpoints); the section renders header-less here so the
 * card isn't re-labelled "Payment Methods" directly under the page title.
 */
export function PaymentMethodsPage(): JSX.Element {
	const { t } = useTranslation();
	const { isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [searchParams] = useSearchParams();
	const [addOpen, setAddOpen] = useState(
		searchParams.get('addPayment') === 'true',
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	const addButton = (
		<button
			type="button"
			onClick={() => {
				setAddOpen(true);
			}}
			className="flex items-center gap-1.5 px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
		>
			<FontAwesomeIcon icon={faPlus} />
			<span>{t('Add')}</span>
		</button>
	);

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader
				title="Payment Methods"
				backPath="/account"
				action={addButton}
			/>
			<AccountBreadcrumb current="Payment Methods" />
			<div className="hidden md:flex items-center justify-between mb-4">
				<h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
					{t('Payment Methods')}
				</h1>
				{addButton}
			</div>
			<PaymentMethodsSection
				hideHeader
				addOpen={addOpen}
				onAddOpenChange={setAddOpen}
			/>
		</div>
	);
}
